
(() => {
  'use strict';

  const SELECTOR = '[data-unbegun-personalizer]';
  const SERVICE_HANDLE = 'unbegun-personalizer';

  let reconciliationRunning = false;

  /*
   * CHECKOUT TRANSLATIONS
   *
   * Customer-facing property names are translated.
   * Internal linking properties remain unchanged.
   */

  const CHECKOUT_LABELS = {
    nl: {
      initials: 'Initialen',
      color: 'Foliekleur',
      product: 'Bij product'
    },
    en: {
      initials: 'Initials',
      color: 'Foil color',
      product: 'For product'
    },
    de: {
      initials: 'Initialen',
      color: 'Folienfarbe',
      product: 'Für Produkt'
    },
    fr: {
      initials: 'Initiales',
      color: 'Couleur du film',
      product: 'Pour le produit'
    }
  };

  function checkoutLabels() {
    const locale = (
      document.documentElement.lang || 'nl'
    ).slice(0, 2).toLowerCase();

    return CHECKOUT_LABELS[locale] || CHECKOUT_LABELS.nl;
  }

  function fieldNameFromSnippet(root, selector, fallback) {
    const name = root
      ?.querySelector(selector)
      ?.getAttribute('name') || '';

    const match = name.match(/^properties\[(.+)\]$/);

    return match?.[1] || fallback;
  }

  function personalizerLabels(root) {
    const defaults = checkoutLabels();

    return {
      initials: fieldNameFromSnippet(
        root,
        '[data-personalizer-property-initials]',
        defaults.initials
      ),
      color: fieldNameFromSnippet(
        root,
        '[data-personalizer-property-color]',
        defaults.color
      ),
      product: defaults.product
    };
  }

  function readTranslatedProperty(properties, kind) {
    const names = [
      ...new Set(
        Object.values(CHECKOUT_LABELS).map(
          labels => labels[kind]
        )
      )
    ];

    for (const name of names) {
      if (
        properties?.[name] !== undefined &&
        properties[name] !== null
      ) {
        return String(properties[name]);
      }
    }

    return '';
  }

  function existingPropertyName(properties, kind, fallback) {
    const names = [
      ...new Set(
        Object.values(CHECKOUT_LABELS).map(
          labels => labels[kind]
        )
      )
    ];

    return names.find(name =>
      Object.prototype.hasOwnProperty.call(
        properties || {},
        name
      )
    ) || fallback;
  }

  function serviceVariantConfig() {
    const root = document.querySelector(SELECTOR);

    return {
      one: Number(root?.dataset.oneLetterVariantId || 0),
      two: Number(root?.dataset.twoLetterVariantId || 0)
    };
  }

  function formatMoney(cents, format) {
    const number = Number(cents || 0);

    const commaValue = (number / 100)
      .toFixed(2)
      .replace('.', ',');

    const dotValue = (number / 100).toFixed(2);

    if (!format) return `€${commaValue}`;

    return format
      .replace(
        /\{\{\s*amount_with_comma_separator\s*\}\}/g,
        commaValue
      )
      .replace(
        /\{\{\s*amount\s*\}\}/g,
        dotValue
      )
      .replace(
        /\{\{\s*amount_no_decimals\s*\}\}/g,
        String(Math.round(number / 100))
      )
      .replace(
        /\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g,
        String(Math.round(number / 100))
      );
  }

  function cleanInitials(value) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z]/g, '')
      .slice(0, 2)
      .toUpperCase();
  }

  function cartRoot() {
    return window.Shopify?.routes?.root || '/';
  }

  async function refreshQuickCart() {
    const quickCart = document.querySelector(
      '[data-quick-cart]'
    );

    const itemsRoot = quickCart?.querySelector('.js-items');
    const footerRoot = quickCart?.querySelector('.js-footer');

    const cartResponse = await fetch(
      `${cartRoot()}cart.js`,
      {
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json'
        }
      }
    );

    const cart = await cartResponse.json();

    document.querySelectorAll(
      '.js-cart-count'
    ).forEach(element => {
      element.textContent = String(cart.item_count || 0);
    });

    if (!quickCart || !itemsRoot) return false;

    const [itemsResponse, footerResponse] = await Promise.all([
      fetch(
        `${cartRoot()}cart?section_id=quick-cart`,
        { credentials: 'same-origin' }
      ),
      fetch(
        `${cartRoot()}cart?section_id=quick-cart-footer`,
        { credentials: 'same-origin' }
      )
    ]);

    if (!itemsResponse.ok) return false;

    const itemsHtml = await itemsResponse.text();
    const itemsContainer = document.createElement('div');

    itemsContainer.innerHTML = itemsHtml;

    const newItems = itemsContainer.querySelector(
      '[data-quick-cart-items]'
    );

    if (newItems) {
      itemsRoot.innerHTML = newItems.innerHTML;
    }

    if (footerRoot && footerResponse.ok) {
      const footerHtml = await footerResponse.text();
      const footerContainer = document.createElement('div');

      footerContainer.innerHTML = footerHtml;

      const newFooter = footerContainer.querySelector(
        '[data-quick-cart-footer]'
      );

      if (newFooter) {
        footerRoot.innerHTML = newFooter.innerHTML;
      }
    }

    return true;
  }

  function isServiceItem(item) {
    return (
      item?.handle === SERVICE_HANDLE ||
      item?.product_handle === SERVICE_HANDLE
    );
  }

  function linkedProperties(item, linkKey) {
    const properties = item?.properties || {};
    const labels = checkoutLabels();

    const initials = readTranslatedProperty(
      properties,
      'initials'
    ).trim().toUpperCase();

    const color = readTranslatedProperty(
      properties,
      'color'
    ).trim() || 'Goud';

    const initialsKey = existingPropertyName(
      properties,
      'initials',
      labels.initials
    );

    const colorKey = existingPropertyName(
      properties,
      'color',
      labels.color
    );

    return {
      [labels.product]:
        item?.product_title ||
        item?.title ||
        'Product',

      [initialsKey]: initials,
      [colorKey]: color,

      '_Personalisatie koppeling': linkKey
    };
  }

  /*
   * PERSONALIZATION QUANTITY RECONCILIATION
   */

  async function reconcilePersonalizationQuantities() {
    if (reconciliationRunning) return false;

    reconciliationRunning = true;

    try {
      const cartResponse = await fetch(
        `${cartRoot()}cart.js?_=${Date.now()}`,
        {
          credentials: 'same-origin',
          cache: 'no-store',
          headers: {
            Accept: 'application/json'
          }
        }
      );

      if (!cartResponse.ok) return false;

      const cart = await cartResponse.json();
      const groups = new Map();
      const config = serviceVariantConfig();

      cart.items.forEach(item => {
        const link = item.properties?.[
          '_Personalisatie koppeling'
        ];

        if (!link) return;

        if (!groups.has(link)) {
          groups.set(link, {
            bases: [],
            services: []
          });
        }

        const group = groups.get(link);

        if (isServiceItem(item)) {
          group.services.push(item);
        } else {
          group.bases.push(item);
        }
      });

      const updates = {};
      const additions = [];

      groups.forEach((group, linkKey) => {
        const desiredQuantity = group.bases.reduce(
          (total, item) =>
            total + Number(item.quantity || 0),
          0
        );

        if (group.services.length) {
          group.services.forEach((service, index) => {
            const desired =
              index === 0 ? desiredQuantity : 0;

            if (
              Number(service.quantity || 0) !== desired
            ) {
              updates[service.key] = desired;
            }
          });
        }

        if (!desiredQuantity || group.services.length) {
          return;
        }

        const base = group.bases[0];

        const initials = readTranslatedProperty(
          base?.properties,
          'initials'
        ).trim();

        const storedVariant = Number(
          base?.properties?.[
            '_Personalisatie variant'
          ] || 0
        );

        const variantId =
          storedVariant ||
          (
            initials.length === 2
              ? config.two
              : config.one
          );

        if (!variantId) return;

        additions.push({
          id: variantId,
          quantity: desiredQuantity,
          properties: linkedProperties(base, linkKey)
        });
      });

      let changed = false;

      if (Object.keys(updates).length) {
        const response = await fetch(
          `${cartRoot()}cart/update.js`,
          {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({ updates })
          }
        );

        changed = response.ok || changed;
      }

      if (additions.length) {
        const response = await fetch(
          `${cartRoot()}cart/add.js`,
          {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({
              items: additions
            })
          }
        );

        changed = response.ok || changed;
      }

      if (changed) {
        window.setTimeout(lockServiceControls, 100);
      }

      return changed;
    } catch (error) {
      console.error(
        '[UNBEGUN Personalizer] Cart synchronization failed',
        error
      );

      return false;
    } finally {
      reconciliationRunning = false;
    }
  }

  /*
   * SERVICE CONTROLS
   */

  function serviceLineContainers(root = document) {
    const containers = new Set();

    root.querySelectorAll(
      'a[href*="/products/unbegun-personalizer"], ' +
      'a[href*="unbegun-personalizer"]'
    ).forEach(link => {
      const container = link.closest(
        '[data-cart-item], [data-line-item], ' +
        '[class*="cart-item"], [class*="cart_item"], ' +
        '[class*="line-item"], [class*="line_item"], li'
      );

      if (container) containers.add(container);
    });

    root.querySelectorAll(
      'h1, h2, h3, h4, p, span, div'
    ).forEach(node => {
      const text = node.textContent
        ?.trim()
        .toLowerCase();

      if (
        text !== 'unbegun services' &&
        text !== 'personalisatie'
      ) {
        return;
      }

      const container = node.closest(
        '[data-cart-item], [data-line-item], ' +
        '[class*="cart-item"], [class*="cart_item"], ' +
        '[class*="line-item"], [class*="line_item"], li'
      );

      if (container) containers.add(container);
    });

    return [...containers];
  }

  function lockServiceControls() {
    serviceLineContainers().forEach(container => {
      container.dataset.personalizationService = 'true';

      container.classList.add(
        'unbegun-personalizer-service-line'
      );

      const controlSelectors = [
        'input[type="number"]',
        'input[name*="quantity"]',
        'button[aria-label*="increase" i]',
        'button[aria-label*="decrease" i]',
        'button[aria-label*="remove" i]',
        'button[title*="remove" i]',
        'a[aria-label*="remove" i]',
        'a[href*="quantity=0"]',
        '[class*="quantity"] button',
        '[class*="quantity"] input',
        '[class*="remove"]'
      ];

      container.querySelectorAll(
        controlSelectors.join(',')
      ).forEach(control => {
        control.setAttribute(
          'aria-hidden',
          'true'
        );

        control.setAttribute(
          'tabindex',
          '-1'
        );

        control.style.setProperty(
          'display',
          'none',
          'important'
        );

        control.style.setProperty(
          'pointer-events',
          'none',
          'important'
        );
      });
    });
  }

  function openQuickCart() {
    const cartToggle = document.querySelector(
      '.js-cart-drawer-toggle'
    );

    const quickCart = document.querySelector(
      '[data-quick-cart]'
    );

    if (!cartToggle || !quickCart) {
      return false;
    }

    cartToggle.dispatchEvent(
      new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        view: window
      })
    );

    return true;
  }

  /*
   * PERSONALIZER INITIALIZATION
   */

  function init(root) {
    if (
      root.dataset.personalizerReady === 'true'
    ) {
      return;
    }

    const form = root.closest(
      'form[data-product-form]'
    );

    if (!form) return;

    root.dataset.personalizerReady = 'true';

    const labels = personalizerLabels(root);

    const toggle = root.querySelector(
      '[data-personalizer-toggle]'
    );

    const content = root.querySelector(
      '[data-personalizer-content]'
    );

    const initialsInput = root.querySelector(
      '[data-personalizer-initials]'
    );

    const counter = root.querySelector(
      '[data-personalizer-counter]'
    );

    const preview = root.querySelector(
      '[data-personalizer-preview]'
    );

    const price = root.querySelector(
      '[data-personalizer-price]'
    );

    const togglePrice = root.querySelector(
      '[data-personalizer-toggle-price]'
    );

    const error = root.querySelector(
      '[data-personalizer-error]'
    );

    const colorInputs = [
      ...root.querySelectorAll(
        '[data-personalizer-color]'
      )
    ];

    const propertyInitials = root.querySelector(
      '[data-personalizer-property-initials]'
    );

    const propertyColor = root.querySelector(
      '[data-personalizer-property-color]'
    );

    const addButton = form.querySelector(
      '[data-add-button]'
    );

    const oneVariantId = Number(
      root.dataset.oneLetterVariantId || 0
    );

    const twoVariantId = Number(
      root.dataset.twoLetterVariantId || 0
    );

    const onePrice = Number(
      root.dataset.oneLetterPrice || 500
    );

    const twoPrice = Number(
      root.dataset.twoLetterPrice || 750
    );

    const moneyFormat =
      root.dataset.moneyFormat ||
      '€{{ amount_with_comma_separator }}';

    let isAdding = false;

    function selectedColor() {
      return colorInputs.find(
        input => input.checked
      )?.value || 'Goud';
    }

    function setError(message = '') {
      if (!error) return;

      error.textContent =
        message || 'Vul één of twee letters in.';

      error.hidden = !message;
    }

    function updateColorUI() {
      colorInputs.forEach(input => {
        const option = input.closest(
          '.unbegun-personalizer__color-option'
        );

        if (option) {
          option.classList.toggle(
            'is-selected',
            input.checked
          );
        }
      });

      const selectedInput = colorInputs.find(
        input => input.checked
      );

      const translatedColor =
        selectedInput?.value || 'Goud';

      const previewColor =
        selectedInput?.dataset.previewColor || 'gold';

      preview.classList.remove(
        'is-gold',
        'is-silver',
        'is-black'
      );

      preview.classList.add(
        `is-${previewColor}`
      );

      propertyColor.value = translatedColor;
    }

    function updateInitialsUI() {
      const initials = cleanInitials(
        initialsInput.value
      );

      initialsInput.value = initials;

      counter.textContent =
        `${initials.length}/2`;

      preview.textContent =
        initials || 'AB';

      preview.style.opacity =
        initials ? '1' : '.42';

      propertyInitials.value = initials;

      const currentPrice =
        initials.length === 2
          ? twoPrice
          : onePrice;

      price.textContent = formatMoney(
        currentPrice,
        moneyFormat
      );

      if (togglePrice) {
        togglePrice.textContent =
          `+ ${formatMoney(
            currentPrice,
            moneyFormat
          )}`;
      }

      setError();

      window.UnbegunGiftWrap
        ?.updateSummary?.(form);
    }

    function updateEnabledState() {
      const enabled = toggle.checked;

      content.hidden = !enabled;

      root.classList.toggle(
        'is-open',
        enabled
      );

      propertyInitials.disabled = !enabled;
      propertyColor.disabled = !enabled;

      setError();

      if (enabled) {
        initialsInput.focus({
          preventScroll: true
        });
      }

      window.UnbegunGiftWrap
        ?.updateSummary?.(form);
    }

    function setLoading(loading) {
      isAdding = loading;

      addButton?.classList.toggle(
        'bttn--loading',
        loading
      );

      if (loading) {
        addButton?.setAttribute(
          'disabled',
          'disabled'
        );
      } else {
        addButton?.removeAttribute(
          'disabled'
        );
      }
    }

    /*
     * ADD PERSONALIZED PRODUCT
     */

    async function addPersonalizedProduct(event) {
      if (!toggle.checked || isAdding) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const initials = cleanInitials(
        initialsInput.value
      );

      if (!initials) {
        setError(
          'Vul één of twee letters in.'
        );

        initialsInput.focus();
        return;
      }

      if (
        window.UnbegunIpadKeyboard?.validate &&
        !window.UnbegunIpadKeyboard.validate(
          form,
          { focus: true }
        )
      ) {
        return;
      }

      if (
        window.UnbegunCustomSize?.validate &&
        !window.UnbegunCustomSize.validate(
          form,
          { focus: true }
        )
      ) {
        return;
      }

      const serviceVariantId =
        initials.length === 1
          ? oneVariantId
          : twoVariantId;

      if (!serviceVariantId) {
        setError(
          'De personalisatietoeslag is nog niet correct gekoppeld.'
        );

        return;
      }

      const baseVariantId = Number(
        form.querySelector('[name="id"]')
          ?.value || 0
      );

      const quantity = Math.max(
        1,
        Number(
          form.querySelector(
            '[name="quantity"]'
          )?.value || 1
        )
      );

      const color = selectedColor();

      const linkKey =
        `UP-${Date.now()}-` +
        Math.random()
          .toString(36)
          .slice(2, 8)
          .toUpperCase();

      if (!baseVariantId) {
        setError(
          'Selecteer eerst een beschikbare uitvoering.'
        );

        return;
      }

      setLoading(true);
      setError();

      const payload = {
        items: [
          {
            id: baseVariantId,
            quantity,
            properties: {
              [labels.initials]: initials,
              [labels.color]: color,

              '_Personalisatie koppeling':
                linkKey,

              '_Personalisatie variant':
                String(serviceVariantId)
            }
          },
          {
            id: serviceVariantId,
            quantity,
            properties: {
              [labels.product]:
                root.dataset.productTitle,

              [labels.initials]: initials,
              [labels.color]: color,

              '_Personalisatie koppeling':
                linkKey
            }
          }
        ]
      };

      /*
       * GIFT WRAPPING
       */

      const giftSelection =
        window.UnbegunGiftWrap
          ?.getSelection?.(form);

      if (giftSelection?.enabled) {
        const giftLink =
          `UG-${Date.now()}-` +
          Math.random()
            .toString(36)
            .slice(2, 8)
            .toUpperCase();

        window.UnbegunGiftWrap.appendToPayload(
          payload,
          form,
          quantity,
          giftLink
        );
      }

      /*
       * IPAD KEYBOARD
       */

      const keyboardSelection =
        window.UnbegunIpadKeyboard
          ?.getSelection?.(form);

      if (keyboardSelection?.enabled) {
        const keyboardLink =
          `UK-${Date.now()}-` +
          Math.random()
            .toString(36)
            .slice(2, 8)
            .toUpperCase();

        window.UnbegunIpadKeyboard.appendToPayload(
          payload,
          form,
          quantity,
          keyboardLink
        );
      }

      /*
       * CUSTOM SIZE
       */

      const customSizeSelection =
        window.UnbegunCustomSize
          ?.getSelection?.(form);

      if (customSizeSelection?.enabled) {
        const customSizeLink =
          `UC-${Date.now()}-` +
          Math.random()
            .toString(36)
            .slice(2, 8)
            .toUpperCase();

        const customSizePayload =
          window.UnbegunCustomSize.appendToPayload(
            payload,
            form,
            quantity,
            customSizeLink
          );

        if (!customSizePayload) {
          setLoading(false);
          return;
        }
      }

      window.UnbegunPreorder
        ?.appendToPayload?.(payload, form);

      /*
       * SUBMIT TO SHOPIFY
       */

      try {
        const response = await fetch(
          `${cartRoot()}cart/add.js`,
          {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              'Content-Type':
                'application/json',

              Accept:
                'application/json',

              'X-Requested-With':
                'XMLHttpRequest'
            },
            body: JSON.stringify(payload)
          }
        );

        const result = await response.json();

        if (
          !response.ok ||
          result.status === 422
        ) {
          throw new Error(
            result.description ||
            'Toevoegen aan winkelwagen is niet gelukt.'
          );
        }

        if (
          window.UnbegunCart
            ?.openAfterMutation
        ) {
          await window.UnbegunCart
            .openAfterMutation();
        } else {
          const quickCartUpdated =
            await refreshQuickCart();

          if (
            !quickCartUpdated ||
            !openQuickCart()
          ) {
            window.location.assign(
              `${cartRoot()}cart`
            );
          }
        }
      } catch (submitError) {
        console.error(
          '[UNBEGUN Personalizer]',
          submitError
        );

        setError(
          submitError.message ||
          'Er ging iets mis. Probeer het opnieuw.'
        );
      } finally {
        setLoading(false);
      }
    }

    /*
     * EVENT LISTENERS
     */

    toggle.addEventListener(
      'change',
      updateEnabledState
    );

    initialsInput.addEventListener(
      'input',
      updateInitialsUI
    );

    colorInputs.forEach(input => {
      input.addEventListener(
        'change',
        updateColorUI
      );
    });

    addButton?.addEventListener(
      'click',
      event => {
        if (toggle.checked) {
          addPersonalizedProduct(event);
        }
      },
      true
    );

    form.addEventListener(
      'submit',
      event => {
        if (toggle.checked) {
          addPersonalizedProduct(event);
        }
      },
      true
    );

    updateEnabledState();
    updateInitialsUI();
    updateColorUI();
  }

  /*
   * INITIALIZATION
   */

  function boot() {
    document.querySelectorAll(
      SELECTOR
    ).forEach(init);
  }

  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      boot
    );
  } else {
    boot();
  }

  document.addEventListener(
    'shopify:section:load',
    boot
  );

  /*
   * PREVENT DIRECT SERVICE MODIFICATIONS
   */

  document.addEventListener(
    'click',
    event => {
      const serviceItem =
        event.target.closest(
          '[data-personalization-service="true"], ' +
          '.unbegun-personalizer-service-line'
        );

      const interactiveControl =
        event.target.closest(
          'button, a, input, select'
        );

      if (
        serviceItem &&
        interactiveControl
      ) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
      }
    },
    true
  );

  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      lockServiceControls,
      { once: true }
    );
  } else {
    lockServiceControls();
  }

  /*
   * PUBLIC API
   */

  window.UnbegunPersonalizer =
    Object.assign(
      window.UnbegunPersonalizer || {},
      {
        reconcile:
          reconcilePersonalizationQuantities,

        refresh:
          refreshQuickCart,

        lockServiceControls
      }
    );
})();